/**
 * Enchantment System - Minecraft-style enchantments for tools and weapons
 *
 * Enchantments are stored on inventory slot objects as:
 *   slot.enchantments = [{ id: 'EFFICIENCY', level: 2 }, ...]
 *
 * Enchantment tiers:
 *   Tier 1: costs 1 XP level  -> low-level enchantment
 *   Tier 2: costs 5 XP levels -> mid-level enchantment
 *   Tier 3: costs 10 XP levels -> high-level enchantment
 *
 * Lapis lazuli cost: tier number (1, 2, or 3 lapis)
 */

import { isTool } from './ToolSystem.js';

// ============================================================
// ENCHANTMENT DEFINITIONS
// ============================================================

export const ENCHANTMENTS = {
  EFFICIENCY: {
    name: 'Eficiencia',
    maxLevel: 5,
    appliesTo: ['pickaxe', 'axe', 'shovel'],
    description: 'Mina mas rapido',
  },
  UNBREAKING: {
    name: 'Irrompible',
    maxLevel: 3,
    appliesTo: ['pickaxe', 'axe', 'shovel', 'sword', 'hoe'],
    description: 'Mayor durabilidad',
  },
  FORTUNE: {
    name: 'Fortuna',
    maxLevel: 3,
    appliesTo: ['pickaxe'],
    description: 'Mas drops al minar',
    conflicts: ['SILK_TOUCH'],
  },
  SHARPNESS: {
    name: 'Filo',
    maxLevel: 5,
    appliesTo: ['sword'],
    description: 'Mas dano',
  },
  SILK_TOUCH: {
    name: 'Toque de Seda',
    maxLevel: 1,
    appliesTo: ['pickaxe'],
    description: 'Mina el bloque tal cual',
    conflicts: ['FORTUNE'],
  },
};

// ============================================================
// ENCHANT COST BY TIER
// ============================================================

/**
 * Get the XP level cost for an enchant tier (1, 2, or 3).
 */
export function getEnchantCost(tier) {
  switch (tier) {
    case 1: return 1;
    case 2: return 5;
    case 3: return 10;
    default: return 1;
  }
}

/**
 * Get the lapis lazuli cost for an enchant tier.
 */
export function getLapisCost(tier) {
  return tier; // 1, 2, or 3
}

// ============================================================
// ENCHANTMENT SELECTION
// ============================================================

/**
 * Get the list of enchantment IDs applicable to a given tool type.
 */
export function getApplicableEnchantments(toolType) {
  const results = [];
  for (const [id, ench] of Object.entries(ENCHANTMENTS)) {
    if (ench.appliesTo.includes(toolType) || ench.appliesTo.includes('all_tools')) {
      results.push(id);
    }
  }
  return results;
}

/**
 * Pick a random enchantment for the given item at the given tier.
 * Avoids conflicts with already-applied enchantments.
 *
 * @param {Object} slot - The inventory slot (must be a tool)
 * @param {number} tier - 1, 2, or 3
 * @returns {{ enchId: string, enchLevel: number } | null}
 */
export function getRandomEnchantment(slot, tier) {
  if (!isTool(slot)) return null;

  const toolType = slot.toolType;
  const applicable = getApplicableEnchantments(toolType);
  if (applicable.length === 0) return null;

  // Get existing enchantments on this item
  const existing = getEnchantments(slot);
  const existingIds = existing.map(e => e.id);

  // Filter out enchantments that conflict with existing ones
  const candidates = applicable.filter(enchId => {
    // Already has this enchantment? Still allow (will upgrade)
    const ench = ENCHANTMENTS[enchId];
    if (ench.conflicts) {
      for (const conflictId of ench.conflicts) {
        if (existingIds.includes(conflictId)) return false;
      }
    }
    return true;
  });

  if (candidates.length === 0) return null;

  // Pick a random one
  const enchId = candidates[Math.floor(Math.random() * candidates.length)];
  const ench = ENCHANTMENTS[enchId];

  // Determine enchantment level based on tier
  let maxForTier;
  if (tier === 1) {
    maxForTier = Math.min(2, ench.maxLevel);
  } else if (tier === 2) {
    maxForTier = Math.min(3, ench.maxLevel);
  } else {
    maxForTier = ench.maxLevel;
  }

  // If item already has this enchantment, try to upgrade
  const existingEnch = existing.find(e => e.id === enchId);
  let enchLevel;
  if (existingEnch) {
    // Upgrade: next level up to maxForTier
    enchLevel = Math.min(existingEnch.level + 1, maxForTier);
  } else {
    // New: random level between 1 and maxForTier
    enchLevel = 1 + Math.floor(Math.random() * maxForTier);
    enchLevel = Math.min(enchLevel, maxForTier);
  }

  return { enchId, enchLevel };
}

/**
 * Generate the three enchantment options (tier 1, 2, 3) for a given item.
 * Returns an array of 3 options, each { enchId, enchLevel, tier, cost, lapisCost } or null.
 */
export function generateEnchantOptions(slot) {
  if (!isTool(slot)) return [null, null, null];

  const options = [];
  for (let tier = 1; tier <= 3; tier++) {
    const result = getRandomEnchantment(slot, tier);
    if (result) {
      options.push({
        ...result,
        tier,
        cost: getEnchantCost(tier),
        lapisCost: getLapisCost(tier),
      });
    } else {
      options.push(null);
    }
  }
  return options;
}

// ============================================================
// ENCHANTMENT APPLICATION
// ============================================================

/**
 * Apply an enchantment to an inventory slot.
 * If the enchantment already exists, upgrades it.
 */
export function applyEnchantment(slot, enchId, enchLevel) {
  if (!slot) return;

  if (!slot.enchantments) {
    slot.enchantments = [];
  }

  // Check if enchantment already exists
  const existing = slot.enchantments.find(e => e.id === enchId);
  if (existing) {
    existing.level = Math.max(existing.level, enchLevel);
  } else {
    slot.enchantments.push({ id: enchId, level: enchLevel });
  }
}

/**
 * Get the enchantments on a slot.
 * @returns {Array<{id: string, level: number}>}
 */
export function getEnchantments(slot) {
  if (!slot || !slot.enchantments) return [];
  return slot.enchantments;
}

/**
 * Check if a slot has a specific enchantment.
 * @returns {number} The enchantment level, or 0 if not present.
 */
export function getEnchantLevel(slot, enchId) {
  const enchs = getEnchantments(slot);
  const found = enchs.find(e => e.id === enchId);
  return found ? found.level : 0;
}

/**
 * Get a display string for all enchantments on a slot (Spanish).
 * E.g. "Eficiencia III, Irrompible II"
 */
export function getEnchantDisplayText(slot) {
  const enchs = getEnchantments(slot);
  if (enchs.length === 0) return '';

  const romanNumerals = ['', 'I', 'II', 'III', 'IV', 'V'];

  return enchs.map(e => {
    const def = ENCHANTMENTS[e.id];
    const name = def ? def.name : e.id;
    const level = romanNumerals[e.level] || String(e.level);
    return `${name} ${level}`;
  }).join(', ');
}

/**
 * Check if a slot is enchantable (must be a tool).
 */
export function isEnchantable(slot) {
  return isTool(slot);
}
